import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as d3 from 'd3';

/**
 * @deprecated This component is deprecated and will be removed in future releases. Please use the new @see ServiceDetailsIssuesFilterComponent instead.
 */
@Component({
    selector: 'service-details-bubble-chart',
    templateUrl: './service-details-bubble-chart.component.html',
    styleUrls: ['./service-details-bubble-chart.component.scss'],
    standalone: false
})
export class ServiceDetailsBubbleChartComponent implements OnInit {
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @Input() data : any = []; 
  @Input() showTooltip: boolean = true;
  @Input() title : string = '';
  @Input() maxBubbles: number = 5;
  @Input() colorRange: [string, string] = ['#61C7E7', '#1A97CD'];
  @Output() bubbleClick = new EventEmitter<string>();

  displayLoader: boolean = false;

  constructor(
    private elementRef: ElementRef
  ) { }

  ngOnInit() {
    this.displayLoader = true;

    if(this.data && this.data.length > 0){
      this.drawChart(this.data);
    }

    this.displayLoader = false;
  }

  // handle logic for click outside
  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    const bubbleD3Elements = this.elementRef.nativeElement.querySelectorAll('#bubble-d3');
    let isOutside = true;
    bubbleD3Elements.forEach((element : Element ) => {
      if (element.contains(event.target  as Node)) {
        isOutside = false;
        return;
      }
    });

    if (this.elementRef.nativeElement.contains(event.target) && isOutside) {
      // Click event occurred outside the bubble chart
      // emit the bubbleClick event as empty string as an indication that no filter is applied
      this.bubbleClick.emit("");
      d3.selectAll("circle").attr("fill","rgb(42, 162, 211)");

    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.displayLoader = true;
      this.data = changes['data'].currentValue;
      this.drawChart(this.data);
      this.displayLoader = false;
    }
  }

  drawChart(bubbleChartData: any[]) {

    this.chartContainer.nativeElement.innerHTML = '';
    const containerWidth = this.chartContainer.nativeElement.offsetWidth; // takes width of the parent element
    const width = containerWidth;
    const height = 350;

    const svg = d3
      .select(this.chartContainer.nativeElement)
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    const maxRadius = Math.min(width, height) / 3;
    const minRadius = maxRadius / 4;

    // Create a tooltip container element only if showTooltip is true
    let tooltip: d3.Selection<HTMLDivElement, unknown, null, undefined>;
    if (this.showTooltip) {
      tooltip = d3.select(this.chartContainer.nativeElement)
        .append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('background-color', 'white')
        .style('padding', '8px');
    }


    // Scale for the bubble sizes
    const radiusScale = d3
      .scaleSqrt()
      .domain([0, d3.max(bubbleChartData, (d: any) => d.callCount)])
      .range([minRadius, maxRadius]);

    // Gradient color scale for the bubbles
    const colorScale: d3.ScaleLinear<string, string> = d3
      .scaleLinear<string>()
      .domain([d3.min(bubbleChartData, (d: any) => d.callCount), d3.max(bubbleChartData, (d: any) => d.callCount)])
      .range(['#61C7E7','#1A97CD']);

    const clipPathId = 'chart-clip-path';

    svg
      .append('defs')
      .append('clipPath')
      .attr('id', clipPathId)
      .append('rect')
      .attr('width', width)
      .attr('height', height);

    const chartArea = svg
      .append('g')
      .attr('clip-path', `url(#${clipPathId})`)
      .attr('id', (d) => 'bubble-d3' )
    const numBubbles = Math.min(bubbleChartData.length, 5);
    const totalBubbleWidth = numBubbles * maxRadius * 2;
    const bubbleSpacing = (width - totalBubbleWidth) / (numBubbles + 1);

    const bubbles = chartArea
      .selectAll('circle')
      .data(bubbleChartData.slice(0, numBubbles))
      .enter()
      .append('g')
      .attr('transform', (d, i) => `translate(${(i + 1) * bubbleSpacing + i * maxRadius * 2 + maxRadius}, ${Math.random() * (height - maxRadius * 2) + maxRadius})`);
    let self = this
    bubbles
      .append('circle')
      .attr('r', (d) => radiusScale(d.callCount))
      .attr('fill', (d) => colorScale(d.callCount))
      .on('mouseenter', (event, d) => {
        if (this.showTooltip && tooltip) {
          tooltip.style('opacity', 1)
            .html(this.getTooltipContent(d))
            .style('left', `${event.pageX + 20}px`)
            .style('top', `${event.pageY + 20}px`);
        }
      })
      .on('mousemove', (event) => {
        if (this.showTooltip && tooltip) {
          tooltip
            .style('left', event.pageX + 20 + 'px')
            .style('top', event.pageY + 20 + 'px');
        }
      })
      .on('mouseleave', () => {
        if (this.showTooltip && tooltip) {
          tooltip
            .style('opacity', 0)
            .style('left', '-999em')
            .style('top', '-999em');
        }
      })
      .on('click', function(event, d) {
        // Retrieve the data from the stored variable and log it
        self.bubbleClick.emit(d.issueName)
        sessionStorage.setItem('currentDataFilter', JSON.stringify(d.issueName));

        // inactive bubble color change
        d3.selectAll("circle")
          .attr("fill","rgb(172, 172, 172)");

        // active bubble color change
        d3.select(this)
          .attr("fill", "#39b54a");
      });

    bubbles
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('alignment-baseline', 'middle')
      .text((d) => d.issueName)
      .attr('fill', 'white')
      .attr('font-size', 14)
      .style('font-weight', '700')
      .each(function (d: any) {
        var text = d3.select(this),
          width = radiusScale(d.callCount) * 1.5,
          x = 0,
          y = radiusScale(d.callCount) * -0.15,
          words = text.text().split(/\s+/).reverse(),
          word,
          line: any = [],
          lineNumber = 0,
          lineHeight = 1.1,
          tspan: any = text.text(null).append("tspan").attr("x", x).attr("y", y);
        while (word = words.pop()) {
          line.push(word);
          tspan.text(line.join(" "));
          if (tspan?.node().getComputedTextLength() > width) {
            line.pop();
            tspan.text(line.join(" "));
            line = [word];
            tspan = text.append("tspan").attr("x", x).attr("y", y).attr("dy", ++lineNumber * lineHeight + "em").text(word);
          }
        }

        //add ellipsis after 3 lines
        const maxLines = 2; // Set the maximum number of lines
        const ellipsis = '...';

        const lines = text.selectAll('tspan').nodes();
        if (lines.length > maxLines) {
          lines.slice(0, maxLines).forEach((line, i) => {
            if (i === maxLines - 1) {
              d3.select(line)
                .text((d) => d3.select(line).text().slice(0, -ellipsis.length) + ellipsis);
            }
          });

          lines.slice(maxLines).forEach((line) => {
            d3.select(line).remove();
          });
        }
    });

    bubbles
    .append("foreignObject")
    .attr("width", (d) => radiusScale(d.callCount) * 2 - 10)
    .attr("height", 30)
    .attr('x', (d) => -radiusScale(d.callCount) + 8)
    .attr('y', (d) => radiusScale(d.callCount) * 0.18)
    .html((d) =>`<div class="bubble-content" style="font-size: 24px; font-weight: 400; color: white; display: flex; align-items: center; justify-content: center;" > ${d.callCount} <img src="assets/white-arrow.svg" /></div>`);
  }

  private getTooltipContent(d: { callCount: any; locations: any; }): string {
    return `
      <div id="bubble-chart-tooltip">
            <div style="font-size: 12px; color: #8E9393; padding-bottom: 5px"><span style="font-size: 30px; font-family: Roboto; font-weight: 300; color: #313233">${d.callCount}</span> total calls</div>
            <div style="font-size: 12px; color: #8E9393;">across<span style="color: #313233;"> ${d.locations} </span>accounts</div>
      </div>
    `;
  }
}
